import { useEffect, useState } from 'react'
import {
  ShieldCheck, Smartphone, KeyRound, Monitor, Check, MessageSquare, BellRing, RotateCcw,
  Mail, MailWarning,
} from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Button, Field, Modal, CopyButton, Rows, Empty } from '../components/UI'
import { useAuth, toast } from '../store'
import { get, post } from '../lib/api'
import { clsx, faDate, toEnDigits, fa, asArray} from '../lib/format'
import { isNativeApp, nativeInfo, setBiometric, setBlockScreenshots } from '../lib/native'

export default function Security() {
  const { user, refresh } = useAuth()
  const [sessions, setSessions] = useState<any[] | null>(null)
  const [twoFa, setTwoFa] = useState<any>(null)
  const [open, setOpen] = useState(false)
  const [pw, setPw] = useState({ old_password: '', new_password: '', confirm: '' })
  const [loading, setLoading] = useState(false)

  // ---- ارسال دوباره‌ی لینک تأیید ایمیل
  const [verifyBusy, setVerifyBusy] = useState(false)
  const sendVerify = async () => {
    setVerifyBusy(true)
    try {
      const r: any = await post('/auth/email/verify/send')
      toast.ok(r?.verified ? 'ایمیل شما از قبل تأیید شده است'
                           : 'لینک تأیید فرستاده شد؛ صندوق ورودی و پوشه هرزنامه را ببینید')
    } catch (e: any) { toast.err(e.message) } finally { setVerifyBusy(false) }
  }

  // ---- شماره موبایل و اعلان پیامکی
  const [mobile, setMobile] = useState('')
  const [smsStep, setSmsStep] = useState<'idle' | 'code'>('idle')
  const [smsCode, setSmsCode] = useState('')
  const [smsBusy, setSmsBusy] = useState(false)

  useEffect(() => { get('/auth/sessions').then((r: any) => setSessions(asArray(r))).catch(() => setSessions([])) }, [])
  useEffect(() => { setMobile(user?.mobile || '') }, [user?.mobile])

  const requestMobileCode = async () => {
    setSmsBusy(true)
    try {
      const r: any = await post('/auth/mobile/request', { mobile: toEnDigits(mobile) })
      setSmsStep('code'); setSmsCode('')
      toast.ok(`کد تأیید به ${r.mobile} پیامک شد`)
      if (r.debugCode) toast.ok(`حالت آزمایشی — کد: ${r.debugCode}`)
    } catch (e: any) { toast.err(e.message) } finally { setSmsBusy(false) }
  }

  const verifyMobile = async () => {
    setSmsBusy(true)
    try {
      await post('/auth/mobile/verify', {
        mobile: toEnDigits(mobile), code: toEnDigits(smsCode), purpose: 'change_mobile',
      })
      toast.ok('شماره موبایل شما تأیید شد')
      setSmsStep('idle'); await refresh()
    } catch (e: any) { toast.err(e.message) } finally { setSmsBusy(false) }
  }

  const setPref = async (key: 'sms_notify' | 'sms_two_fa', value: boolean) => {
    try {
      await post('/auth/notify-prefs', { [key]: value })
      toast.ok('تنظیمات ذخیره شد'); await refresh()
    } catch (e: any) { toast.err(e.message) }
  }

  const openTwoFa = async () => {
    try { setTwoFa(await post('/auth/2fa/setup')); setOpen(true) }
    catch (e: any) { toast.err(e.message) }
  }

  const toggle = async () => {
    try {
      const r = await post('/auth/2fa/toggle')
      toast.ok(r.enabled ? 'ورود دومرحله‌ای فعال شد' : 'ورود دومرحله‌ای غیرفعال شد')
      await refresh(); setOpen(false)
    } catch (e: any) { toast.err(e.message) }
  }

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) return toast.err('تکرار رمز عبور مطابقت ندارد')
    setLoading(true)
    try {
      await post('/auth/change-password', { old_password: pw.old_password, new_password: pw.new_password })
      toast.ok('رمز عبور با موفقیت تغییر کرد')
      setPw({ old_password: '', new_password: '', confirm: '' })
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  return (
    <PanelShell title="امنیت و تنظیمات" subtitle="حساب خود را با ابزارهای امنیتی ارزینو محافظت کنید">
      {/* این کارت فقط داخل اپ اندروید دیده می‌شود — در مرورگر معنایی ندارد */}
      <AppSecurityCard />
      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <div className="mb-4 flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-aqua/30 bg-aqua/10 text-aqua">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-bold text-white">ورود دومرحله‌ای (۲FA)</h3>
              <p className="mt-1 text-xs leading-6 text-slate-400">
                با فعال‌سازی این گزینه، هنگام ورود و برداشت، کد یکبارمصرف برنامه Authenticator درخواست می‌شود.
              </p>
            </div>
          </div>
          <div className="mb-4 flex items-center justify-between rounded-xl border border-white/[.06] bg-white/[.02] px-3 py-2.5 text-xs">
            <span className="text-slate-500">وضعیت</span>
            <span className={clsx('font-semibold', user?.twoFa ? 'text-up' : 'text-slate-400')}>
              {user?.twoFa ? 'فعال' : 'غیرفعال'}
            </span>
          </div>
          <Button variant={user?.twoFa ? 'ghost' : 'aqua'} className="w-full" onClick={openTwoFa}>
            <Smartphone className="h-4 w-4" /> {user?.twoFa ? 'مدیریت / غیرفعال‌سازی' : 'فعال‌سازی ورود دومرحله‌ای'}
          </Button>
        </Card>

        <Card>
          <div className="mb-4 flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-brand-500/30 bg-brand-500/10 text-brand-300">
              <KeyRound className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-bold text-white">تغییر رمز عبور</h3>
              <p className="mt-1 text-xs leading-6 text-slate-400">رمز عبور قوی و منحصربه‌فرد انتخاب کنید.</p>
            </div>
          </div>
          <Field label="رمز فعلی">
            <input className="input" type="password" dir="ltr" value={pw.old_password}
              onChange={(e) => setPw({ ...pw, old_password: e.target.value })} />
          </Field>
          <Field label="رمز جدید">
            <input className="input" type="password" dir="ltr" value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
          </Field>
          <Field label="تکرار رمز جدید">
            <input className="input" type="password" dir="ltr" value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
          </Field>
          <Button loading={loading} onClick={changePassword} className="w-full">ذخیره رمز جدید</Button>
        </Card>
      </div>

      {/* ---------------- شماره موبایل و اعلان پیامکی ---------------- */}
      <Card className="mt-5">
        <div className="mb-4 flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-up/30 bg-up/10 text-up">
            <MessageSquare className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-bold text-white">شماره موبایل و اطلاع‌رسانی پیامکی</h3>
            <p className="mt-1 text-xs leading-6 text-slate-400">
              با تأیید شماره، کد ورود و اطلاع واریز و برداشت برای شما پیامک می‌شود.
            </p>
          </div>
          <span className={clsx('mr-auto shrink-0 rounded-full border px-2.5 py-1 text-[11px]',
            user?.mobileVerified ? 'border-up/25 bg-up/10 text-up'
              : 'border-gold/25 bg-gold/10 text-gold')}>
            {user?.mobileVerified ? 'تأیید شده' : 'تأیید نشده'}
          </span>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <Field label="شماره موبایل">
              <div className="flex gap-2">
                <input className="input num" dir="ltr" placeholder="09xxxxxxxxx" value={mobile}
                  disabled={smsStep === 'code'} onChange={(e) => setMobile(e.target.value)} />
                {smsStep === 'idle' ? (
                  <Button variant="ghost" loading={smsBusy} onClick={requestMobileCode}
                    className="shrink-0 !px-4">
                    {user?.mobileVerified && toEnDigits(mobile) === user?.mobile ? 'ارسال مجدد' : 'تأیید شماره'}
                  </Button>
                ) : (
                  <Button variant="ghost" className="shrink-0 !px-3" onClick={() => setSmsStep('idle')}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Field>

            {smsStep === 'code' && (
              <Field label="کد پیامک‌شده">
                <div className="flex gap-2">
                  <input className="input num tracking-[.4em]" dir="ltr" maxLength={8} value={smsCode}
                    onChange={(e) => setSmsCode(toEnDigits(e.target.value).replace(/\D/g, ''))} />
                  <Button loading={smsBusy} onClick={verifyMobile} className="shrink-0 !px-4">
                    <Check className="h-4 w-4" /> تأیید
                  </Button>
                </div>
              </Field>
            )}
          </div>

          <div className="space-y-2.5">
            <button onClick={() => setPref('sms_notify', !user?.smsNotify)}
              className={clsx('flex w-full items-start justify-between gap-3 rounded-xl border p-3 text-right transition',
                user?.smsNotify ? 'border-brand-500/35 bg-brand-500/[.07]' : 'border-white/[.06] bg-white/[.02]')}>
              <div>
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <BellRing className="h-3.5 w-3.5" /> اطلاع‌رسانی پیامکی واریز و برداشت
                </div>
                <div className="mt-1 text-[10px] leading-5 text-slate-500">
                  پیام‌های امنیتی مثل کد ورود همیشه ارسال می‌شوند.
                </div>
              </div>
              <span className={clsx('relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition',
                user?.smsNotify ? 'bg-gradient-to-l from-brand-600 to-brand-400' : 'bg-white/10')}>
                <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
                  style={{ right: user?.smsNotify ? 2 : 18 }} />
              </span>
            </button>

            {/* اعلان ایمیلی + وضعیت تأیید نشانی */}
            <button onClick={() => setPref('email_notify', !user?.emailNotify)}
              className={clsx('flex w-full items-start justify-between gap-3 rounded-xl border p-3 text-right transition',
                user?.emailNotify ? 'border-brand-500/35 bg-brand-500/[.07]' : 'border-white/[.06] bg-white/[.02]')}>
              <div>
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <Mail className="h-3.5 w-3.5" /> اطلاع‌رسانی ایمیلی
                </div>
                <div className="mt-1 text-[10px] leading-5 text-slate-500">
                  همان اعلان‌ها به <span className="num" dir="ltr">{user?.email}</span> هم فرستاده می‌شود.
                  پیام‌های امنیتی همیشه ارسال می‌شوند.
                </div>
              </div>
              <span className={clsx('relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition',
                user?.emailNotify ? 'bg-gradient-to-l from-brand-600 to-brand-400' : 'bg-white/10')}>
                <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
                  style={{ right: user?.emailNotify ? 2 : 18 }} />
              </span>
            </button>

            {!user?.emailVerified && (
              <div className="rounded-xl border border-gold/25 bg-gold/[.06] p-3">
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <MailWarning className="h-3.5 w-3.5 text-gold" /> ایمیل شما هنوز تأیید نشده
                </div>
                <p className="mt-1 text-[10px] leading-5 text-slate-400">
                  تا تأیید نشدن، فقط پیام‌های امنیتی به ایمیل شما فرستاده می‌شود.
                </p>
                <Button variant="ghost" className="mt-2 !py-1.5 !text-[11px]"
                  loading={verifyBusy} onClick={sendVerify}>
                  ارسال دوباره لینک تأیید
                </Button>
              </div>
            )}

            <button onClick={() => setPref('sms_two_fa', !user?.smsTwoFa)}
              className={clsx('flex w-full items-start justify-between gap-3 rounded-xl border p-3 text-right transition',
                user?.smsTwoFa ? 'border-brand-500/35 bg-brand-500/[.07]' : 'border-white/[.06] bg-white/[.02]')}>
              <div>
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <ShieldCheck className="h-3.5 w-3.5" /> کد پیامکی هنگام ورود
                </div>
                <div className="mt-1 text-[10px] leading-5 text-slate-500">
                  علاوه بر رمز عبور، یک کد یکبارمصرف پیامکی هم خواسته می‌شود.
                </div>
              </div>
              <span className={clsx('relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition',
                user?.smsTwoFa ? 'bg-gradient-to-l from-brand-600 to-brand-400' : 'bg-white/10')}>
                <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
                  style={{ right: user?.smsTwoFa ? 2 : 18 }} />
              </span>
            </button>
          </div>
        </div>
      </Card>

      <Card className="mt-5 !p-0">
        <div className="border-b border-white/[.06] p-4">
          <h3 className="font-bold text-white">تاریخچه ورود به حساب</h3>
        </div>
        <div className="p-2">
          {sessions === null ? <div className="p-3"><Rows n={4} /></div>
            : sessions.length === 0 ? <Empty text="ورودی ثبت نشده است" icon={<Monitor className="h-6 w-6" />} />
            : sessions.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-3 border-b border-white/[.04] px-3 py-3 last:border-0">
                <Monitor className="h-4 w-4 text-slate-500" />
                <span className="num text-xs text-slate-300" dir="ltr">{s.ip}</span>
                <span className="max-w-[22rem] truncate text-[11px] text-slate-500" dir="ltr">{s.agent}</span>
                <span className="mr-auto text-[11px] text-slate-500">{faDate(s.at)}</span>
              </div>
            ))}
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="ورود دومرحله‌ای">
        <div className="space-y-4">
          <p className="text-xs leading-6 text-slate-400">
            کد زیر را در برنامه Google Authenticator یا هر اپلیکیشن TOTP دیگری وارد کنید، سپس ورود دومرحله‌ای را فعال کنید.
          </p>
          <div className="rounded-2xl border border-white/[.07] bg-ink-900/60 p-4 text-center">
            <div className="num mb-3 select-all break-all text-lg font-bold tracking-widest text-brand-200" dir="ltr">
              {twoFa?.secret}
            </div>
            <CopyButton text={twoFa?.secret ?? ''} label="کپی کلید" />
          </div>
          <div className="rounded-xl border border-gold/20 bg-gold/[.06] p-3 text-[11px] leading-6 text-gold/90">
            این کلید را در جای امنی نگهداری کنید. در صورت از دست دادن دسترسی به برنامه، بازیابی حساب نیازمند احراز هویت مجدد است.
          </div>
          <Button variant={user?.twoFa ? 'down' : 'up'} className="w-full !py-3" onClick={toggle}>
            <Check className="h-4 w-4" /> {user?.twoFa ? 'غیرفعال‌سازی ۲FA' : 'فعال‌سازی ۲FA'}
          </Button>
        </div>
      </Modal>
    </PanelShell>
  )
}

/* ═════════════════════════════════ تنظیمات مخصوص اپ اندروید */
function AppSecurityCard() {
  const [info, setInfo] = useState(() => nativeInfo())

  // در مرورگر معمولی هیچ‌چیز نشان داده نمی‌شود
  if (!isNativeApp()) return null

  const toggleLock = () => {
    const next = !info.biometricEnabled
    if (!setBiometric(next)) { toast.err('نسخه اپ شما این قابلیت را ندارد؛ اپ را به‌روز کنید'); return }
    setInfo(nativeInfo(true))
    toast.ok(next ? 'قفل اپ روشن شد' : 'قفل اپ خاموش شد')
  }

  const toggleShots = () => {
    const next = !info.blockScreenshots
    if (!setBlockScreenshots(next)) { toast.err('نسخه اپ شما این قابلیت را ندارد'); return }
    setInfo(nativeInfo(true))
  }

  const Row = ({ title, body, on, onToggle, disabled, hint }: any) => (
    <div className="flex flex-wrap items-center gap-3 border-b border-white/[.05] py-3.5 last:border-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-white">{title}</div>
        <p className="mt-1 text-[11px] leading-6 text-slate-400">{disabled ? hint : body}</p>
      </div>
      <button
        onClick={onToggle}
        disabled={disabled}
        className={clsx('relative h-7 w-12 shrink-0 rounded-full transition disabled:opacity-40',
          on ? 'bg-brand-500' : 'bg-white/15')}>
        <span className={clsx('absolute top-1 h-5 w-5 rounded-full bg-white transition-all',
          on ? 'right-1' : 'right-6')} />
      </button>
    </div>
  )

  return (
    <Card className="mb-5">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Smartphone className="h-4 w-4 text-brand-300" />
        <h3 className="font-bold text-white">امنیت اپلیکیشن</h3>
        <span className="chip !py-0.5 text-[10px] text-slate-400">نسخه {fa(info.version)}</span>
      </div>

      <Row
        title="قفل اپ با اثر انگشت"
        body="هر بار که اپ را باز می‌کنید، هویت شما تأیید می‌شود. اگر گوشی لحظه‌ای دست کسی افتاد، حساب شما باز نمی‌شود."
        hint="گوشی شما اثر انگشت یا رمز صفحه ثبت‌شده ندارد؛ ابتدا از تنظیمات گوشی آن را فعال کنید."
        on={info.biometricEnabled}
        disabled={!info.biometricAvailable}
        onToggle={toggleLock}
      />
      <Row
        title="جلوگیری از عکس‌گرفتن از صفحه"
        body="مانع تصویربرداری از صفحه و دیده‌شدن موجودی در فهرست اپ‌های باز می‌شود. توصیه می‌شود روشن بماند."
        on={info.blockScreenshots}
        onToggle={toggleShots}
      />
    </Card>
  )
}
